import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../config/api';
import { notifyUnauthorized } from '../utils/apiAuth';

/**
 * Récupère un fichier protégé de l'API sous forme d'`ArrayBuffer`.
 *
 * Pourquoi. Le lecteur natif passait l'URL de `/api/books/{id}/file` à epub.js
 * et à react-pdf : ces bibliothèques téléchargent elles-mêmes, SANS en-tête
 * `Authorization`. Sous authentification, le livre partait en 401 et le lecteur
 * restait vide. On télécharge donc par `apiFetch` (qui pose l'en-tête) et l'on
 * remet les octets aux visionneuses. Le jeton ne passe JAMAIS en paramètre
 * d'URL : il fuiterait dans les journaux, le cache et le `Referer`.
 *
 * @param {string|null} endpoint - chemin API (sans préfixe /api), ou null.
 * @returns {{ data: ArrayBuffer|null, loading: boolean, error: string|null, status: number|null, retry: () => void }}
 *   `status` : code HTTP de l'échec (404 = fichier original absent du stockage),
 *   `null` sans réponse HTTP (réseau) ou sans erreur. Le lecteur s'en sert pour
 *   basculer sur le texte extrait plutôt que d'afficher une erreur bloquante.
 */
export default function useFichierApi(endpoint) {
  // `pour` mémorise l'endpoint auquel l'état se rapporte : au changement de
  // livre, le rendu qui précède l'effet ne doit pas servir les octets de
  // l'ancien (un PDF remis à epub.js, par exemple).
  const [etat, setEtat] = useState({ pour: null, data: null, error: null, status: null });
  const [essai, setEssai] = useState(0);

  useEffect(() => {
    if (!endpoint) {
      setEtat({ pour: null, data: null, error: null, status: null });
      return undefined;
    }

    const controleur = new AbortController();
    setEtat({ pour: endpoint, data: null, error: null, status: null });

    (async () => {
      let statutEchec = null;
      try {
        const reponse = await apiFetch(endpoint, { signal: controleur.signal });
        if (!reponse.ok) {
          if (reponse.status === 401) notifyUnauthorized();
          statutEchec = reponse.status;
          throw new Error(
            reponse.status === 401
              ? 'Session expirée : reconnectez-vous pour ouvrir ce livre.'
              : reponse.status === 404
                ? 'Fichier du livre introuvable.'
                : `Impossible de charger le fichier (HTTP ${reponse.status}).`,
          );
        }
        const data = await reponse.arrayBuffer();
        if (!controleur.signal.aborted) setEtat({ pour: endpoint, data, error: null, status: null });
      } catch (err) {
        // Démontage ou changement de livre : aucun état à publier.
        if (controleur.signal.aborted) return;
        setEtat({
          pour: endpoint,
          data: null,
          error: err?.message || 'Impossible de charger le fichier du livre.',
          status: statutEchec,
        });
      }
    })();

    return () => controleur.abort();
  }, [endpoint, essai]);

  const retry = useCallback(() => setEssai((n) => n + 1), []);

  const courant = endpoint && etat.pour === endpoint;
  return {
    data: courant ? etat.data : null,
    error: courant ? etat.error : null,
    status: courant ? etat.status : null,
    loading: Boolean(endpoint) && (!courant || (!etat.data && !etat.error)),
    retry,
  };
}
